import React, { useState, useRef, useEffect } from 'react';

const CharacterTagOverlay = ({ 
  artworkUrl, 
  taggedCharacters = [], 
  showTags = true,
  onTagClick = null,
  className = "",
  draggable = false,
  onTagPositionChange = null
}) => {
  const [hoveredTag, setHoveredTag] = useState(null);
  const [draggingTag, setDraggingTag] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Drag logic - unified handler for both mouse and touch
  const handleStart = (e, tag) => {
    if (!draggable) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    const tagX = (tag.x / 100) * rect.width;
    const tagY = (tag.y / 100) * rect.height;
    setDragOffset({ x: mouseX - tagX, y: mouseY - tagY });
    setDraggingTag(tag.id);
  };

  const handleMove = (e) => {
    if (!draggable || !draggingTag || !containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = ((clientX - rect.left - dragOffset.x) / rect.width) * 100;
    const y = ((clientY - rect.top - dragOffset.y) / rect.height) * 100;
    if (onTagPositionChange) {
      onTagPositionChange(draggingTag, Math.max(5, Math.min(95, x)), Math.max(5, Math.min(95, y)));
    }
  };

  const handleEnd = () => {
    setDraggingTag(null);
    setDragOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (draggable && draggingTag) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    }
  }, [draggable, draggingTag, dragOffset]);

  // Helper to get label for tag
  const getTagLabel = (tag) => tag.name || tag.objectName || tag.surroundingsReason || '';

  if (!showTags || !taggedCharacters || taggedCharacters.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className={`absolute inset-0 w-full h-full pointer-events-auto ${className}`} style={{ zIndex: 20 }}>
      {/* Character tags overlay */}
      {taggedCharacters.map(tag => (
        <div
          key={tag.id}
          className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 z-30 ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
          style={{
            left: `${tag.x}%`,
            top: `${tag.y}%`,
            pointerEvents: 'auto'
          }}
          onMouseEnter={() => setHoveredTag(tag.id)}
          onMouseLeave={() => setHoveredTag(null)}
          onClick={() => onTagClick && onTagClick(tag)}
          onMouseDown={e => handleStart(e, tag)}
          onTouchStart={e => handleStart(e, tag)}
        >
          {/* Tag marker */}
          <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-white font-bold text-sm shadow-lg bg-amber-400 border-amber-600 hover:scale-110 transition-transform duration-200">
            {getTagLabel(tag).charAt(0).toUpperCase()}
          </div>
          {/* Tag label - always visible */}
          <div className="absolute top-10 left-1/2 transform -translate-x-1/2 bg-white border border-amber-200 rounded-lg px-2 py-1 text-xs font-semibold text-amber-800 shadow-md whitespace-nowrap z-40 pointer-events-none">
            <div className="font-bold">{getTagLabel(tag)}</div>
            {tag.age && <div className="text-xs text-amber-600">Age: {tag.age}</div>}
            {tag.gender && <div className="text-xs text-amber-600">Gender: {tag.gender}</div>}
            {tag.appearance && <div className="text-xs text-amber-600 max-w-32 truncate" title={tag.appearance}>
              {tag.appearance}
            </div>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CharacterTagOverlay; 
